// Items editor — list + form. Master/detail shell delegated to
// `createCatalogueEditor`. This file owns: blank/sanitize, the per-row
// form (identity / requirements / category-dependent stats / affixes
// sub-editor / stacking), default-slot mapping, and a live Zod
// validation pane.

import {
  type Affix,
  type AffixType,
  EQUIP_SLOTS,
  type EquipSlot,
  type ItemCategory,
  ItemTemplateSchema,
  type ItemTemplate,
} from "@vinyl/shared";
import {
  type CatalogueEditor,
  createCatalogueEditor,
} from "./catalogue-editor.js";
import { el } from "./dom.js";
import {
  checkboxField,
  numberField,
  readonlyField,
  selectField,
  textField,
} from "./form-fields.js";

export interface ItemsScreenCallbacks {
  onDirtyChange: (dirty: boolean) => void;
}

export interface ItemsScreen {
  root: HTMLElement;
  setItems(items: readonly ItemTemplate[]): void;
  // Snapshot the current draft (cleaned for the wire). Used by the publish
  // button. Calling this doesn't mutate any state.
  getDraft(): ItemTemplate[];
  isDirty(): boolean;
  markPublished(): void;
}

const CATEGORIES: ItemCategory[] = [
  "weapon1H",
  "weapon2H",
  "shield",
  "armor",
  "ring",
  "neck",
  "wrist",
  "cloak",
  "consumable",
  "misc",
];

const STAT_KEYS = ["str", "dex", "con", "int", "fai", "rap"] as const;
type ReqKey = (typeof STAT_KEYS)[number];

const CONSUMABLE_KINDS = [
  "heal",
  "restoreManaArc",
  "restoreManaDiv",
  "food",
] as const;

const WEAPON_TYPES = ["lightMelee", "heavyMelee", "bow", "staff"] as const;

// Subtype options for a given affix type. `null` = no subtype (hp, critPct).
// M8 — single mana pool (no subType), 7 resists (physical + 6 schools).
const AFFIX_SUBTYPES: Record<AffixType, readonly string[] | null> = {
  stat: ["str", "dex", "con", "int", "fai", "rap"],
  dmgPct: ["melee", "ranged", "spell"],
  hp: null,
  critPct: null,
  mana: null,
  resistPct: [
    "physical",
    "divine",
    "necromancy",
    "water",
    "fire",
    "air",
    "earth",
  ],
};

function blankItem(): ItemTemplate {
  return {
    id: "new_item",
    name: "New Item",
    category: "misc",
    slot: null,
    weight: 0.1,
    requirements: {},
    stats: {},
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 1,
  };
}

// Clean a draft into a wire-ready ItemTemplate (strip empty optional
// objects so the strict Zod schema doesn't reject).
function sanitize(draft: ItemTemplate): ItemTemplate {
  const reqs: Partial<Record<ReqKey, number>> = {};
  for (const k of STAT_KEYS) {
    const v = draft.requirements[k];
    if (v !== undefined && v > 0) reqs[k] = v;
  }
  const stats: ItemTemplate["stats"] = {};
  if (draft.stats.damage && draft.stats.damage.min !== undefined) {
    stats.damage = {
      min: Math.max(0, Math.floor(draft.stats.damage.min)),
      max: Math.max(0, Math.floor(draft.stats.damage.max ?? draft.stats.damage.min)),
    };
  }
  if (draft.stats.weaponType) stats.weaponType = draft.stats.weaponType;
  if (draft.stats.defense !== undefined && draft.stats.defense > 0) {
    stats.defense = Math.floor(draft.stats.defense);
  }
  if (draft.stats.consumable?.kind && draft.stats.consumable.amount !== undefined) {
    stats.consumable = {
      kind: draft.stats.consumable.kind,
      amount: Math.max(0, Math.floor(draft.stats.consumable.amount)),
    };
  }
  // Strip affixes with value=0 or missing required subType (uniform handling
  // for typos / half-filled rows).
  const affixes = (draft.affixes ?? []).filter((a) => {
    if (a.value === 0) return false;
    if (
      (a.type === "stat" ||
        a.type === "dmgPct" ||
        a.type === "mana" ||
        a.type === "resistPct") &&
      !a.subType
    ) {
      return false;
    }
    return true;
  });
  const iconPath = draft.iconPath?.trim() ?? "";
  return {
    id: draft.id,
    name: draft.name,
    category: draft.category,
    slot: draft.slot,
    weight: draft.weight,
    requirements: reqs,
    stats,
    stackable: draft.stackable,
    maxStack: draft.maxStack,
    rarity: draft.rarity,
    value: draft.value,
    ...(affixes.length > 0 ? { affixes } : {}),
    ...(iconPath ? { iconPath } : {}),
  };
}

export function createItemsScreen(cb: ItemsScreenCallbacks): ItemsScreen {
  const editor: CatalogueEditor<ItemTemplate> = createCatalogueEditor<ItemTemplate>(
    {
      entityName: "item",
      getName: (it) => it.name,
      blank: blankItem,
      sanitize,
      renderEditor: ({ draft, markDirty, rerender }) =>
        renderForm(draft, markDirty, rerender),
    },
    { onDirtyChange: cb.onDirtyChange },
  );

  function renderForm(
    draft: ItemTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const container = el("div", {});

    const cat = draft.category;
    const showDamage = cat === "weapon1H" || cat === "weapon2H";
    const showDefense =
      cat === "shield" || cat === "armor" || cat === "cloak" || cat === "wrist";
    const showConsumable = cat === "consumable";
    const showSlot = cat === "armor"; // other categories have canonical slots
    const showStackable = cat === "consumable" || cat === "misc";

    // --- Basic fields ---
    const idInput = textField(
      "id (lowercase, _-separated)",
      draft.id,
      (v) => {
        draft.id = v;
        markDirty();
      },
      "[a-z][a-z0-9_]{2,40}",
    );
    const nameInput = textField("Name", draft.name, (v) => {
      draft.name = v;
      markDirty();
    });
    const categoryInput = selectField("Category", CATEGORIES, draft.category, (v) => {
      draft.category = v as ItemCategory;
      // Auto-adjust slot for the new category.
      draft.slot = defaultSlotForCategory(draft.category);
      // Auto-flip stackable+maxStack for consumables / non-consumables.
      if (draft.category === "consumable" && !draft.stackable) {
        draft.stackable = true;
        draft.maxStack = 99;
      } else if (draft.category !== "consumable" && draft.stackable) {
        draft.stackable = false;
        draft.maxStack = 1;
      }
      markDirty();
      rerender();
    });

    let slotField: HTMLElement;
    if (showSlot) {
      slotField = selectField(
        "Slot",
        [...EQUIP_SLOTS],
        (draft.slot as EquipSlot) ?? "Chest",
        (v) => {
          draft.slot = v as EquipSlot;
          markDirty();
        },
      );
    } else {
      slotField = readonlyField(
        "Slot (auto from category)",
        Array.isArray(draft.slot)
          ? draft.slot.join(" + ")
          : draft.slot === null
          ? "—"
          : draft.slot,
      );
    }

    const weightInput = numberField("Weight (kg)", draft.weight, (v) => {
      draft.weight = Math.max(0, v);
      markDirty();
    }, 0.1);
    const valueInput = numberField("Value (copper)", draft.value, (v) => {
      draft.value = Math.max(0, Math.floor(v));
      markDirty();
    });
    const rarityInput = selectField(
      "Rarity",
      ["common", "rare", "unique"],
      draft.rarity,
      (v) => {
        draft.rarity = v as "common" | "rare" | "unique";
        markDirty();
      },
    );

    const iconInput = textField(
      "Icon path (relative to client/public/assets/)",
      draft.iconPath ?? "",
      (v) => {
        const trimmed = v.trim();
        if (trimmed) draft.iconPath = trimmed;
        else delete draft.iconPath;
        markDirty();
      },
    );

    container.append(
      el("h2", {}, draft.name || "(unnamed)"),
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Identity"),
        el(
          "div",
          { class: "editor-grid" },
          idInput,
          nameInput,
          categoryInput,
          slotField,
          rarityInput,
          weightInput,
          valueInput,
          iconInput,
        ),
      ),
    );

    // --- Requirements ---
    const reqsGrid = el("div", { class: "editor-grid" });
    for (const stat of STAT_KEYS) {
      const cur = draft.requirements[stat] ?? 0;
      reqsGrid.append(
        numberField(stat.toUpperCase(), cur, (v) => {
          if (v <= 0) delete draft.requirements[stat];
          else draft.requirements[stat] = Math.floor(v);
          markDirty();
        }),
      );
    }
    container.append(
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Stat requirements (0 = no req)"),
        reqsGrid,
      ),
    );

    // --- Stats ---
    if (showDamage || showDefense || showConsumable) {
      const statsGrid = el("div", { class: "editor-grid" });
      if (showDamage) {
        const dmg = draft.stats.damage ?? { min: 1, max: 1 };
        statsGrid.append(
          numberField("Damage min", dmg.min, (v) => {
            draft.stats.damage = { ...(draft.stats.damage ?? { min: 0, max: 0 }), min: Math.max(0, Math.floor(v)) };
            markDirty();
          }),
          numberField("Damage max", dmg.max, (v) => {
            draft.stats.damage = { ...(draft.stats.damage ?? { min: 0, max: 0 }), max: Math.max(0, Math.floor(v)) };
            markDirty();
          }),
          selectField("Weapon type", [...WEAPON_TYPES], draft.stats.weaponType ?? "lightMelee", (v) => {
            draft.stats.weaponType = v as (typeof WEAPON_TYPES)[number];
            markDirty();
          }),
        );
      }
      if (showDefense) {
        statsGrid.append(
          numberField("Defense", draft.stats.defense ?? 0, (v) => {
            if (v <= 0) delete draft.stats.defense;
            else draft.stats.defense = Math.floor(v);
            markDirty();
          }),
        );
      }
      if (showConsumable) {
        const cur = draft.stats.consumable ?? { kind: "heal" as const, amount: 0 };
        statsGrid.append(
          selectField("Consumable kind", [...CONSUMABLE_KINDS], cur.kind, (v) => {
            draft.stats.consumable = { ...cur, kind: v as (typeof CONSUMABLE_KINDS)[number] };
            markDirty();
          }),
          numberField("Consumable amount", cur.amount, (v) => {
            draft.stats.consumable = { ...cur, amount: Math.max(0, Math.floor(v)) };
            markDirty();
          }),
        );
      }
      container.append(
        el(
          "div",
          { class: "editor-section" },
          el("h3", {}, "Stats"),
          statsGrid,
        ),
      );
    }

    // --- Affixes (template-level fixed bonuses, M7.1.1) ---
    // Skipped for stackables / non-equippables since they can't carry bonuses.
    if (!draft.stackable && draft.slot !== null) {
      container.append(renderAffixesSection(draft, markDirty, rerender));
    }

    // --- Stackable ---
    if (showStackable) {
      container.append(
        el(
          "div",
          { class: "editor-section" },
          el("h3", {}, "Stacking"),
          el(
            "div",
            { class: "editor-grid" },
            checkboxField("Stackable", draft.stackable, (v) => {
              draft.stackable = v;
              if (!v) draft.maxStack = 1;
              else if (draft.maxStack < 2) draft.maxStack = 99;
              markDirty();
              rerender();
            }),
            numberField("Max stack", draft.maxStack, (v) => {
              draft.maxStack = Math.max(1, Math.floor(v));
              markDirty();
            }),
          ),
        ),
      );
    }

    // --- Validation panel + delete ---
    const validationEl = el("div", { class: "error" });
    function refreshValidation(): void {
      const parsed = ItemTemplateSchema.safeParse(sanitize(draft));
      validationEl.textContent = parsed.success
        ? ""
        : "Invalid: " + parsed.error.issues.map((i) => `${i.path.join(".")} ${i.message}`).join("; ");
    }
    refreshValidation();
    container.addEventListener("input", refreshValidation);
    container.addEventListener("change", refreshValidation);

    const deleteButton = el(
      "button",
      {
        class: "button danger",
        onclick: () => {
          if (!confirm(`Delete "${draft.name}" from the draft? Publish to apply.`)) return;
          const next = editor.getDraft().filter((d) => d.id !== draft.id);
          editor.set(next);
          cb.onDirtyChange(true);
        },
      },
      "Delete from draft",
    );

    container.append(
      el(
        "div",
        { class: "editor-section" },
        el("h3", {}, "Validation"),
        validationEl,
      ),
      el("div", { class: "editor-actions" }, deleteButton),
    );

    return container;
  }

  function renderAffixesSection(
    draft: ItemTemplate,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const list = el("div", { class: "affix-list" });
    const affixes = draft.affixes ?? [];

    for (let idx = 0; idx < affixes.length; idx++) {
      const a = affixes[idx]!;
      list.append(renderAffixRow(draft, idx, a, markDirty, rerender));
    }

    const addBtn = el(
      "button",
      {
        class: "button secondary",
        type: "button",
        onclick: () => {
          if (!draft.affixes) draft.affixes = [];
          draft.affixes.push({ type: "stat", subType: "str", value: 1 });
          markDirty();
          rerender();
        },
      },
      "+ Add affix",
    );

    const hint = el(
      "div",
      { class: "affix-hint" },
      "Template affixes are applied to every instance. Set rarity to ",
      el("b", {}, "unique"),
      " to mark named gear.",
    );

    return el(
      "div",
      { class: "editor-section" },
      el("h3", {}, `Affixes (${affixes.length}/5)`),
      list,
      el("div", { class: "affix-actions" }, addBtn),
      hint,
    );
  }

  function renderAffixRow(
    draft: ItemTemplate,
    idx: number,
    affix: Affix,
    markDirty: () => void,
    rerender: () => void,
  ): HTMLElement {
    const subtypes = AFFIX_SUBTYPES[affix.type];
    const row = el("div", { class: "affix-row" });

    // Type selector
    const typeSel = el(
      "select",
      {
        onchange: (e: Event) => {
          const newType = (e.target as HTMLSelectElement).value as AffixType;
          const allowed = AFFIX_SUBTYPES[newType];
          const next: Affix = {
            type: newType,
            value: affix.value,
            ...(allowed ? { subType: allowed[0] as Affix["subType"] } : {}),
          };
          draft.affixes![idx] = next;
          markDirty();
          rerender();
        },
      },
    );
    for (const t of ["stat", "dmgPct", "hp", "critPct", "mana", "resistPct"] as AffixType[]) {
      const opt = el("option", { value: t }, t);
      if (t === affix.type) (opt as HTMLOptionElement).selected = true;
      typeSel.append(opt);
    }
    row.append(typeSel);

    // SubType (only if applicable)
    if (subtypes) {
      const subSel = el(
        "select",
        {
          onchange: (e: Event) => {
            draft.affixes![idx] = {
              ...affix,
              subType: (e.target as HTMLSelectElement).value as Affix["subType"],
            };
            markDirty();
          },
        },
      );
      for (const s of subtypes) {
        const opt = el("option", { value: s }, s);
        if (s === affix.subType) (opt as HTMLOptionElement).selected = true;
        subSel.append(opt);
      }
      row.append(subSel);
    } else {
      row.append(el("span", { class: "affix-no-sub" }, "—"));
    }

    // Value input
    const valInput = el("input", {
      type: "number",
      value: String(affix.value),
      step: "1",
      oninput: (e: Event) => {
        const raw = (e.target as HTMLInputElement).value;
        const v = Number.parseInt(raw, 10);
        if (Number.isFinite(v)) {
          draft.affixes![idx] = { ...affix, value: v };
          markDirty();
        }
      },
    });
    row.append(valInput);

    // Remove
    const removeBtn = el(
      "button",
      {
        class: "button danger",
        type: "button",
        title: "Remove affix",
        onclick: () => {
          draft.affixes!.splice(idx, 1);
          if (draft.affixes!.length === 0) delete draft.affixes;
          markDirty();
          rerender();
        },
      },
      "×",
    );
    row.append(removeBtn);

    return row;
  }

  return {
    root: editor.root,
    setItems(items) {
      editor.set(items.map((it) => ({ ...it })));
    },
    getDraft: editor.getDraft,
    isDirty: editor.isDirty,
    markPublished: editor.markPublished,
  };
}

function defaultSlotForCategory(c: ItemCategory): EquipSlot | EquipSlot[] | null {
  switch (c) {
    case "weapon1H":
      return "RightHand";
    case "weapon2H":
      return ["RightHand", "LeftHand"];
    case "shield":
      return "LeftHand";
    case "armor":
      return "Chest";
    case "ring":
      return "Ring1";
    case "neck":
      return "Neck";
    case "wrist":
      return "Wrist1";
    case "cloak":
      return "Cloak";
    case "consumable":
      return null;
    case "misc":
      return null;
  }
}
